import React, { useMemo } from "react";
import "./Home.css";

const ANDROID_URL = "https://shalom.short.gy";
const IOS_WEB_URL = "https://shalom.short.gy";

function getPlatformUrl() {
  if (typeof navigator === "undefined") return IOS_WEB_URL;

  const ua = navigator.userAgent || "";
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);

  if (isAndroid) return ANDROID_URL;
  if (isIOS) return IOS_WEB_URL;

  return IOS_WEB_URL;
}

const problemPoints = [
  {
    title: "Hiring takes too long",
    text: "Families can spend days or weeks calling bureaus, asking friends, and repeating the same details before meeting a suitable househelp.",
  },
  {
    title: "Trust is still manual",
    text: "Many homes still hire through word-of-mouth, with limited visibility on the bureau, the worker, reviews, or replacement terms.",
  },
  {
    title: "Pricing is unclear",
    text: "Parents and busy households often do not know what salary range to expect, what the agency fee covers, or what happens if the worker leaves.",
  },
];

const solutionPoints = [
  {
    title: "Browse vetted options",
    text: "Open the app, view available househelps or Mama Fua services, and compare options before starting the conversation.",
  },
  {
    title: "Contact the bureau directly",
    text: "Once you find someone suitable, tap the message icon to speak to the registered bureau for interviews and next steps.",
  },
  {
    title: "Know the cost upfront",
    text: "See practical salary ranges, agency-fee guidance, and replacement terms before committing.",
  },
  {
    title: "Create accountability",
    text: "Orders, ratings, reviews, and support channels help keep the process traceable for families, bureaus, and workers.",
  },
];

const pricingCards = [
  {
    name: "Live-in nannies",
    price: "KES 8,000–15,000",
    period: "per month",
    note: "Final salary depends on duties, experience, home setup, and agreement with the bureau.",
  },
  {
    name: "Dayburgs",
    price: "KES 12,000–20,000",
    period: "per month",
    note: "Best for 3–5 days a week where the worker comes during the day and leaves in the evening.",
  },
  {
    name: "Occasional day work",
    price: "KES 699–2,099",
    period: "per day",
    note: "Useful for one-off cleaning, laundry, or short-term help depending on availability.",
  },
];

const tractionItems = [
  {
    metric: "24+ years",
    label: "Bureau experience behind the model",
  },
  {
    metric: "30 mins",
    label: "Fast matching goal for available home services",
  },
  {
    metric: "M-Pesa",
    label: "Local payment flow built for Kenyan households",
  },
  {
    metric: "Partner bureaus",
    label: "A marketplace model built with agencies, not around them",
  },
];

const Home = () => {
  const appUrl = useMemo(() => getPlatformUrl(), []);
  const isAndroid =
    typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");

  const primaryCtaLabel = isAndroid ? "Get it on Google Play" : "Get the App";

  return (
    <main className="shalom">
      {/* HERO */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-copy">
            <span className="pill">Trusted domestic help • Kenya</span>

            <h1>
              Find a reliable <span className="accent">househelp</span> without
              the weeks of stress.
            </h1>

            <p className="hero-lead">
              Shalom helps Kenyan families discover vetted domestic workers through
              registered partner bureaus, understand the costs upfront, and move
              from search to interview faster.
            </p>

            <div className="hero-cta">
              <a
                className="btn btn-primary"
                href={appUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {primaryCtaLabel}
              </a>

              <a className="btn btn-ghost" href="#pricing">
                View pricing
              </a>
            </div>

            <div className="trust-row">
              <div className="trust-item">
                <span className="dot" />
                Vetted workers
              </div>
              <div className="trust-item">
                <span className="dot" />
                Registered bureaus
              </div>
              <div className="trust-item">
                <span className="dot" />
                Replacement support
              </div>
            </div>
          </div>

          <aside className="hero-card" aria-label="Shalom quick summary">
            <div className="hero-card-top">
              <span className="card-kicker">What Shalom changes</span>
              <h2>From guessing to guided hiring.</h2>
              <p>
                Browse, compare, message a bureau, interview, agree on terms, and
                proceed with more confidence.
              </p>
            </div>

            <div className="mini-stats">
              <div className="mini-stat">
                <strong>01</strong>
                <span>Browse available workers</span>
              </div>
              <div className="mini-stat">
                <strong>02</strong>
                <span>Message the bureau</span>
              </div>
              <div className="mini-stat">
                <strong>03</strong>
                <span>Interview and confirm</span>
              </div>
            </div>

            <p className="mini-note">
              Tip: Always confirm duties, salary, off-days, and replacement terms
              before the worker reports.
            </p>
          </aside>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="section">
        <div className="container split">
          <div className="section-heading">
            <span className="eyebrow">The problem</span>
            <h2 className="section-title">
              Domestic hiring is still too slow, risky, and unclear.
            </h2>
            <p className="section-subtitle">
              The need is urgent, but the process has not kept up. Families want
              trust. Bureaus want serious clients. Good workers want fair,
              consistent opportunities.
            </p>
          </div>

          <div className="problem-list">
            {problemPoints.map((item) => (
              <article className="problem-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="section section-soft">
        <div className="container">
          <div className="section-heading center">
            <span className="eyebrow">The solution</span>
            <h2 className="section-title">
              Shalom brings the bureau process online.
            </h2>
            <p className="section-subtitle">
              We are not replacing trusted agencies. We are giving them a clearer,
              faster, more accountable way to connect with families looking for
              reliable domestic support.
            </p>
          </div>

          <div className="grid solution-grid">
            {solutionPoints.map((item) => (
              <article className="card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="section">
        <div className="container">
          <div className="section-heading center">
            <span className="eyebrow">How it works</span>
            <h2 className="section-title">A simple path from search to interview.</h2>
          </div>

          <ol className="steps">
            <li className="step">
              <div className="step-badge">1</div>
              <div className="step-body">
                <h3>Download the Shalom App</h3>
                <p>
                  Get started on the app and browse vetted househelps, dayburgs,
                  and Mama Fua options.
                </p>
              </div>
            </li>

            <li className="step">
              <div className="step-badge">2</div>
              <div className="step-body">
                <h3>Choose someone you are interested in</h3>
                <p>
                  Review the available options, compare details, and shortlist the
                  worker or service that fits your home.
                </p>
              </div>
            </li>

            <li className="step">
              <div className="step-badge">3</div>
              <div className="step-body">
                <h3>Message the bureau directly</h3>
                <p>
                  Tap the message icon to contact the partner bureau for interviews,
                  more details, and final confirmation.
                </p>
              </div>
            </li>

            <li className="step">
              <div className="step-badge">4</div>
              <div className="step-body">
                <h3>Agree, pay, and rate</h3>
                <p>
                  Confirm the terms, use the available payment flow where applicable,
                  and leave a rating after service to support accountability.
                </p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="section section-soft pricing-section">
        <div className="container">
          <div className="section-heading center">
            <span className="eyebrow">Pricing guide</span>
            <h2 className="section-title">Know the expected cost before you call.</h2>
            <p className="section-subtitle">
              Actual rates may vary by duties, location, experience, number of
              working days, and the final agreement with the bureau.
            </p>
          </div>

          <div className="pricing-grid">
            {pricingCards.map((item) => (
              <article className="price-card" key={item.name}>
                <h3>{item.name}</h3>
                <div className="price">{item.price}</div>
                <span>{item.period}</span>
                <p>{item.note}</p>
              </article>
            ))}
          </div>

          <div className="fee-box">
            <div>
              <span className="eyebrow">Agency fee example</span>
              <h3>How the first month works</h3>
              <p>
                The agency fee is half of the nanny’s monthly salary. To make it
                easier, half of that fee is deducted from the nanny’s first salary.
              </p>
            </div>

            <div className="fee-example">
              <div className="fee-row">
                <span>Example salary</span>
                <strong>KES 8,000</strong>
              </div>
              <div className="fee-row">
                <span>Agency fee</span>
                <strong>KES 4,000</strong>
              </div>
              <div className="fee-row">
                <span>Client pays upfront</span>
                <strong>KES 4,000</strong>
              </div>
              <div className="fee-row">
                <span>First-month nanny take-home</span>
                <strong>KES 6,000</strong>
              </div>
              <div className="fee-row total">
                <span>From month two</span>
                <strong>KES 8,000/month</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REPLACEMENT POLICY */}
      <section className="section">
        <div className="container policy-banner">
          <div>
            <span className="eyebrow">Replacement policy</span>
            <h2>If the nanny leaves within the first month, you are not left stranded.</h2>
            <p>
              Our partner bureau replaces the nanny within the agreed first-month
              window. You only pay for the days worked, with no extra replacement
              fee under the policy.
            </p>
          </div>

          <a
            className="btn btn-primary"
            href={appUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Start on Shalom
          </a>
        </div>
      </section>

      {/* TRACTION */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-heading center light">
            <span className="eyebrow">Traction & credibility</span>
            <h2 className="section-title">
              Built from real bureau experience, not boardroom theory.
            </h2>
            <p className="section-subtitle">
              Shalom is shaped by the day-to-day realities of Kenyan households,
              domestic workers, and bureaus that have handled this problem for years.
            </p>
          </div>

          <div className="traction-grid">
            {tractionItems.map((item) => (
              <article className="traction-card" key={item.metric}>
                <strong>{item.metric}</strong>
                <span>{item.label}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section final-cta">
        <div className="container cta-banner">
          <div>
            <span className="eyebrow">Ready to get started?</span>
            <h2>Find help for your home with more confidence.</h2>
            <p>
              Download Shalom, browse vetted househelps, and message a bureau when
              you are ready to interview.
            </p>
          </div>

          <a
            className="btn btn-primary"
            href={appUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {primaryCtaLabel}
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="container footer-inner">
          <p>© {new Date().getFullYear()} Shalom Domestic Agencies</p>
          <div className="footer-links">
            <a href="/privacy">Privacy</a>
            <a href="/deletion">Delete Account</a>
            <a href="mailto:shalomke98@gmail.com">Support</a>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default Home;